import { useEffect, useState } from 'react'
import { FileText, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { financeService, type FinanceProjectDetail } from '@/services/financeService'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { FinanceProposalSnapshotView } from '@/components/Financeiro/FinanceProposalSnapshotView'
import { toast } from 'sonner'

export function ClienteAgradaProjectExpand({
  projectId,
  onUpdated,
}: {
  projectId: string
  onUpdated?: () => void
}) {
  const [detail, setDetail] = useState<FinanceProjectDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    void financeService
      .getProject(projectId)
      .then((data) => {
        if (active) setDetail(data)
      })
      .catch(() => {
        if (active) setDetail(null)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [projectId])

  const handleUpload = async () => {
    if (!file) return
    setUploading(true)
    try {
      await financeService.uploadContract(projectId, file)
      toast.success('Contrato anexado')
      setFile(null)
      const refreshed = await financeService.getProject(projectId)
      setDetail(refreshed)
      onUpdated?.()
    } catch {
      toast.error('Falha ao enviar contrato')
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <div className='flex justify-center py-6'>
        <div className='h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent' />
      </div>
    )
  }

  if (!detail) {
    return <p className='text-sm text-text-muted'>Não foi possível carregar detalhes do projeto.</p>
  }

  return (
    <div className='mt-3 space-y-4 rounded-lg border border-red-500/20 bg-red-500/[0.04] p-4'>
      <div className='grid gap-2 text-sm sm:grid-cols-2'>
        <div>
          <span className='text-text-muted'>Status:</span>{' '}
          <span className='font-medium'>{formatFinanceStatus(detail.status)}</span>
        </div>
        <div>
          <span className='text-text-muted'>Origem:</span>{' '}
          <span>{detail.source.replace(/_/g, ' ')}</span>
        </div>
        {detail.deal && (
          <div className='sm:col-span-2'>
            <span className='text-text-muted'>Deal:</span> <span>{detail.deal.nome}</span>
          </div>
        )}
      </div>

      <section>
        <h4 className='mb-2 text-sm font-semibold'>Contratos</h4>
        {detail.contracts.length === 0 ? (
          <p className='text-sm text-text-muted'>Nenhum contrato anexado.</p>
        ) : (
          <ul className='space-y-2'>
            {detail.contracts.map((c) => (
              <li
                key={c.id}
                className='flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.03] p-3'
              >
                <FileText size={16} className='shrink-0 text-text-muted' />
                <div className='min-w-0 flex-1'>
                  <p className='font-medium'>Versão {c.version}</p>
                  <p className='text-xs text-text-muted'>
                    {c.uploadedBy?.name ?? '—'} · {new Date(c.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                <a
                  href={resolvePublicAssetUrl(c.fileUrl)}
                  target='_blank'
                  rel='noreferrer'
                  className='text-xs text-accent hover:underline'
                >
                  Abrir PDF
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className='rounded-lg border border-dashed border-white/[0.08] p-3'>
        <h4 className='mb-2 text-sm font-semibold'>Anexar contrato (PDF)</h4>
        <input
          type='file'
          accept='application/pdf'
          className='mb-3 w-full text-sm'
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button type='button' size='sm' disabled={!file || uploading} onClick={() => void handleUpload()}>
          <Upload size={14} />
          {uploading ? 'Enviando…' : 'Enviar contrato'}
        </Button>
      </section>

      {detail.proposalSnapshots[0] && (
        <FinanceProposalSnapshotView snapshot={detail.proposalSnapshots[0]} />
      )}
    </div>
  )
}
